import React, { useState, useEffect, useRef } from "react"
import PropTypes from "prop-types"
import { Marker as GoogleMarker, InfoBox } from "@react-google-maps/api"

import activePolylineSubject from "utils/activePolylineSubject"
import bouncingSubject from "utils/bouncingSubject"
import PointPinNormal from "../mapAssets/point-pin-normal.png"
import PointPinHover from "../mapAssets/point-pin-hover.png"
import PointPinSelected from "../mapAssets/point-pin-selected.png"
import DestinationPin from "../mapAssets/destination-pin.png"
import BaseNormal from "../mapAssets/neutral-marker-base.png"
import BaseHover from "../mapAssets/hover-marker-base.png"

import {
  StyledInfoBoxWrapper,
  StyledCloseIcon,
  StyledCloseButton,
} from "./Marker.styles"

const markerSizes = {
  active: [24, 24],
  hidden: [24, 24],
  destination: [24, 24],
  hover: [24, 24],
  exposed: [16, 16],
  normal: [24, 24],
  custom: [16, 16],
  baseNormal: [24, 24],
  baseHover: [30, 30],
  customHighlight: [12, 12],
  customAccessibility: [16, 16],
}
const markerOptions = {
  baseNormal: { zIndex: 100 },
  baseHover: { zIndex: 100 },
  custom: { zIndex: 101 },
  customHighlight: { zIndex: 101 },
  customAccessibility: { zIndex: 101 },
  normal: { zIndex: 101 },
  hover: { zIndex: 101 },
  active: { zIndex: 102 },
  destination: { zIndex: 103 },
  hidden: { opacity: 0 },
}

const Marker = ({
  onClick,
  onInfoBoxClick,
  id,
  isSelected,
  pointType,
  markerIcon,
  infoBoxContent,
  overlay,
  isHighlighted,
  onInfoBoxVisibilityChange,
  getMarkerLocation,
  setShowSpotlight,
  ...rest
}) => {
  const pointsTypes = {
    active: PointPinSelected,
    hidden: PointPinSelected,
    destination: DestinationPin,
    hover: PointPinHover,
    exposed: PointPinHover,
    normal: PointPinNormal,
    custom: markerIcon,
    customHighlight: markerIcon,
    customAccessibility: markerIcon,
    baseNormal: BaseNormal,
    baseHover: BaseHover,
  }
  const [marker, setMarker] = useState(null)
  const [markerIconType, setMarkerIconType] = useState(pointType)
  const [isInfoBoxVisible, setInfoBoxVisibility] = useState(false)
  const [infoBoxPosition, setInfoBoxPosition] = useState(null)
  const [isBouncing, setIsBouncing] = useState(false)

  const infoBoxRef = useRef(null)

  const calculateBoxPosition = () => {
    const overlayProjection = overlay.getProjection()
    const overlaySize = overlayProjection.j

    const markerPt = overlayProjection.fromLatLngToDivPixel(
      marker.getPosition()
    )
    setInfoBoxPosition({
      ...markerPt,
      boxWidth: infoBoxRef.current.offsetWidth,
      overlayWidth: overlaySize.wa,
    })
  }

  useEffect(() => {
    if (pointType === "active" || pointType === "hidden") {
      bouncingSubject.attach(setIsBouncing)
    }

    return bouncingSubject.detach(setIsBouncing)
  }, [])

  const showMarker = () => {
    let opacity = 0
    const interval = setInterval(() => {
      marker.setOptions({
        opacity,
      })
      opacity += 0.0625

      if (opacity >= 1) {
        clearInterval(interval)
        marker.setOptions({
          opacity: 1,
        })
      }
    }, 10)
  }

  const hideMarker = () => {
    let opacity = marker.getOpacity()
    const interval = setInterval(() => {
      marker.setOptions({
        opacity,
      })
      opacity -= 0.0625
      if (opacity <= 0) {
        clearInterval(interval)
        marker.setOptions({
          opacity: 0,
        })
      }
    }, 10)
  }

  useEffect(() => {
    if (marker) {
      if (isBouncing) {
        marker.setAnimation(1)

        if (pointType === "hidden") {
          showMarker()
        }
      } else {
        marker.setAnimation(null)

        if (pointType === "hidden") {
          hideMarker()
        }
      }
    }
  }, [isBouncing, marker])

  useEffect(() => {
    setMarkerIconType(isSelected ? "active" : pointType)
  }, [isSelected])

  useEffect(() => {
    // eslint-disable-next-line no-unused-expressions
    isHighlighted && marker && calculateBoxPosition()
    setInfoBoxVisibility(isHighlighted)
  }, [isHighlighted])

  const handleClose = (event) => {
    event.stopPropagation()
    onInfoBoxVisibilityChange(false)
    setInfoBoxVisibility(false)
  }

  const handleOutterClick = (event) => {
    if (isInfoBoxVisible) {
      handleClose(event)
    }
  }

  useEffect(() => {
    if (infoBoxRef.current) {
      infoBoxRef.current.addEventListener("mouseleave", handleClose)
    }
    return () => {
      if (infoBoxRef.current) {
        infoBoxRef.current.removeEventListener("mouseleave", handleClose)
      }
    }
  }, [infoBoxRef.current, infoBoxRef])

  useEffect(() => {
    if (infoBoxContent) {
      document.addEventListener("click", handleOutterClick)
    }

    if (!isSelected && pointType === "normal") {
      if (isInfoBoxVisible) {
        setMarkerIconType("hover")
      } else {
        setMarkerIconType("normal")
      }
    }

    return () => document.removeEventListener("click", handleOutterClick)
  }, [isInfoBoxVisible])

  const getMarkerPixelPosition = () => {
    const overlayProjection = overlay.getProjection()

    if (marker) {
      return overlayProjection.fromLatLngToDivPixel(marker.getPosition())
    }
    return null
  }

  const handleMarkerMouseOver = () => {
    if (infoBoxContent) {
      calculateBoxPosition()
      setInfoBoxVisibility(true)
      onInfoBoxVisibilityChange(true)
    }

    if (pointType === "customAccessibility") {
      activePolylineSubject.notify({ action: "hover", value: rest.name })
    }

    if (pointType === "active") {
      setShowSpotlight(true)
      getMarkerLocation(getMarkerPixelPosition())
    }
  }

  const handleMarkerMouseOut = () => {
    if (pointType === "customAccessibility") {
      activePolylineSubject.notify({ action: "hover", value: null })
    }
  }

  const handleMarkerClick = () => {
    document.getElementById("map").click()
    if (infoBoxContent) {
      calculateBoxPosition()
      setInfoBoxVisibility(true)
    } else {
      onClick(id, marker.getPosition())
    }

    if (pointType === "customAccessibility") {
      activePolylineSubject.notify({ action: "click", value: rest.name })
    }
  }

  const handleLoad = (googleMarker) => {
    setMarker(googleMarker)
  }

  return (
    <>
      {markerIconType === "customHighlight" && (
        <GoogleMarker
          className={`${markerIconType}-${
            isInfoBoxVisible ? "baseHover" : "baseNormal"
          }`}
          icon={{
            scaledSize: new window.google.maps.Size(
              ...markerSizes[isInfoBoxVisible ? "baseHover" : "baseNormal"]
            ),
            url: pointsTypes[isInfoBoxVisible ? "baseHover" : "baseNormal"],
            anchor: new window.google.maps.Point(
              markerSizes[isInfoBoxVisible ? "baseHover" : "baseNormal"][0] / 2,
              markerSizes[isInfoBoxVisible ? "baseHover" : "baseNormal"][1] / 2
            ),
          }}
          options={
            markerOptions[isInfoBoxVisible ? "baseHover" : "baseNormal"] || {}
          }
          {...rest}
        />
      )}
      <GoogleMarker
        key={`Marker${id}`}
        className={id}
        icon={{
          scaledSize: new window.google.maps.Size(
            ...markerSizes[markerIconType]
          ),
          url: pointsTypes[markerIconType],
          anchor: new window.google.maps.Point(
            markerSizes[markerIconType][0] / 2,
            markerSizes[markerIconType][1] / 2
          ),
        }}
        onLoad={handleLoad}
        onClick={handleMarkerClick}
        onMouseOver={handleMarkerMouseOver}
        onMouseOut={handleMarkerMouseOut}
        options={markerOptions[markerIconType] || {}}
        {...rest}
      />
      {infoBoxContent && (
        <InfoBox
          key={`InfoBox${id}`}
          position={rest.position}
          options={{
            pixelOffset: new window.google.maps.Size(0, 4),
            disableAutoPan: true,
            visible: isInfoBoxVisible,
            enableEventPropagation: true,
            zIndex: 101,
          }}
        >
          <StyledInfoBoxWrapper
            position={infoBoxPosition}
            ref={infoBoxRef}
            onClick={() => onInfoBoxClick(marker.getPosition())}
          >
            {infoBoxContent}
            <StyledCloseButton onClick={handleClose}>
              <StyledCloseIcon />
            </StyledCloseButton>
          </StyledInfoBoxWrapper>
        </InfoBox>
      )}
    </>
  )
}

Marker.propTypes = {
  onClick: PropTypes.func,
  isSelected: PropTypes.bool,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  pointType: PropTypes.string,
  markerIcon: PropTypes.oneOfType([
    PropTypes.shape({
      original: PropTypes.string,
      large: PropTypes.string,
      medium: PropTypes.string,
      small: PropTypes.string,
    }),
    PropTypes.string,
  ]),
  infoBoxContent: PropTypes.node,
  overlay: PropTypes.any,
  isHighlighted: PropTypes.bool,
  onInfoBoxClick: PropTypes.func,
  onInfoBoxVisibilityChange: PropTypes.func,
  getMarkerLocation: PropTypes.func,
  setShowSpotlight: PropTypes.func,
}

Marker.defaultProps = {
  isSelected: false,
  pointType: "normal",
  onClick: () => {},
  infoBoxContent: null,
  markerIcon: null,
  overlay: null,
  isHighlighted: false,
  onInfoBoxClick: () => {},
  onInfoBoxVisibilityChange: () => {},
  getMarkerLocation: () => {},
  setShowSpotlight: () => {},
}

export default Marker
